// Libraries
import { useEffect } from 'react';

// Hooks
import { useAppSelector, useAppDispatch } from '../../../app/hooks';

// Features
import { putUserAsync, selectUser } from '../../../features/user/userSlice';
import {
    getGameModsAsync,
    pushMenuStatus,
    selectGameMods,
} from '../../../features/app/appSlice';

// Components
import Menu from '../Menu/Menu';
import ToggleSwitch from '../../atoms/ToggleSwitch/ToggleSwitch';
import ToggleCircle from '../../atoms/ToggleCircle/ToggleCircle';

// Styles
import styles from './Settings.module.css';

// Constants
const colors: { name: string; slug: string }[] = [
    {
        name: 'Ruby red',
        slug: 'ruby',
    },
    {
        name: 'Teal',
        slug: 'teal',
    },
    {
        name: 'Red',
        slug: 'red',
    },
    {
        name: 'Green',
        slug: 'green',
    },
    {
        name: 'Lilac',
        slug: 'lilac',
    },
    {
        name: 'Sky blue',
        slug: 'blue-sky',
    },
    {
        name: 'Pink',
        slug: 'pink',
    },
    {
        name: 'Blue',
        slug: 'blue',
    },
    {
        name: 'Coral',
        slug: 'coral',
    },
    {
        name: 'Purple',
        slug: 'primary',
    },
    {
        name: 'Orange',
        slug: 'orange',
    },
    {
        name: 'Violet',
        slug: 'violet',
    },
];

const icons: { id: number; type: string; name: string; slug: string }[] = [
    {
        id: 0,
        type: 'dauber',
        name: 'None',
        slug: '',
    },
    {
        id: 1,
        type: 'dauber',
        name: 'Bingoport',
        slug: 'bingoport',
    },
    {
        id: 2,
        type: 'dauber',
        name: 'Bingoport (White)',
        slug: 'bingoport-white',
    },
];

const voices: { id: number; type: string; name: string; slug: string }[] = [
    {
        id: 0,
        type: 'dauber',
        name: 'Default',
        slug: 'default',
    },
];

/**
 * @function Settings
 * Settings panel
 *
 * @example
 * return (
 *   <Settings />
 * )
 */
function Settings(): JSX.Element {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const gameMods = useAppSelector(selectGameMods);
    const iconMods = gameMods.filter(({ type }) => type === 'dauber');
    const voiceMods = gameMods.filter(({ type }) => type === 'voice');
    const iconModsExpanded = [...icons, ...iconMods];
    const voiceModsExpanded = [...voices, ...voiceMods];
    const modIdPrefix = '/api/v2/game_mods/';
    const userIconMods = iconMods.filter(({ id }) =>
        user.game_mods.find(
            (item) => parseInt(item.replace(modIdPrefix, '')) === id,
        ),
    );
    const userVoiceMods = voiceMods.filter(({ id }) =>
        user.game_mods.find(
            (item) => parseInt(item.replace(modIdPrefix, '')) === id,
        ),
    );

    useEffect(() => {
        dispatch(getGameModsAsync());
    }, []);

    function handleMenuClick(value: boolean) {
        dispatch(pushMenuStatus(value));
    }

    /**
     * @function handleSettingChange
     * Trigger an update to the user's settings
     *
     * @param prop The target settings property
     * @param value The new setting value
     */
    function handleSettingChange(prop: string, value: any) {
        dispatch(
            putUserAsync({
                user: { ...user, [prop]: value },
            }),
        );
    }

    return (
        <Menu
            header={<h2>Game Settings</h2>}
            onClose={() => handleMenuClick(false)}
        >
            <div className={styles.settings}>
                <div className={styles.switch}>
                    <h3>General Sounds</h3>
                    <ToggleSwitch
                        text=""
                        checked={user.sound_on}
                        onChange={() =>
                            handleSettingChange('sound_on', !user.sound_on)
                        }
                    />
                </div>
                <div className={styles.switch}>
                    <h3>Heartbeat Sound</h3>
                    <ToggleSwitch
                        text=""
                        checked={user.heartbeat_on}
                        onChange={() =>
                            handleSettingChange(
                                'heartbeat_on',
                                !user.heartbeat_on,
                            )
                        }
                    />
                </div>
                <div className={styles.switch}>
                    <h3>Chat</h3>
                    <ToggleSwitch
                        text=""
                        checked={user.chat_on}
                        onChange={() =>
                            handleSettingChange('chat_on', !user.chat_on)
                        }
                    />
                </div>
                <div className={styles.switch}>
                    <h3>Flashboard</h3>
                    <ToggleSwitch
                        text=""
                        checked={user.flashcard_on}
                        onChange={() =>
                            handleSettingChange(
                                'flashcard_on',
                                !user.flashcard_on,
                            )
                        }
                    />
                </div>
                <h3>Caller voice</h3>
                <div className={styles.circles}>
                    {voiceModsExpanded.map(
                        ({
                            id,
                            name,
                            slug,
                        }: {
                            id: number;
                            name: string;
                            slug: string;
                        }) => {
                            let disabled = true;
                            let checked = false;

                            // If the voice mod is part of the default set of voice mods
                            if (
                                voices.filter((item) => item.id === id).length >
                                0
                            ) {
                                disabled = false;
                            }

                            // If the voice mod is in the list of user's available voice mods
                            if (
                                user.game_mods.filter(
                                    (item) =>
                                        parseInt(
                                            item.replace(modIdPrefix, ''),
                                        ) === id,
                                ).length > 0
                            ) {
                                disabled = false;
                            }

                            if (
                                // If the user's voice setting matches the voice mod slug
                                user.voice === slug ||
                                // If the user's voice setting is empty and this is the default mod
                                (user.voice === '' && slug === 'default')
                            ) {
                                checked = true;
                            }

                            return (
                                <ToggleCircle
                                    key={id}
                                    text={name}
                                    backgroundColor={slug}
                                    name="voice"
                                    value={slug}
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() =>
                                        handleSettingChange('voice', slug)
                                    }
                                />
                            );
                        },
                    )}
                </div>
                {userVoiceMods.length !== voiceMods.length && (
                    <p>
                        You can purchase more caller voices at the{' '}
                        <a
                            href={`https://${process.env.REACT_APP_LOGIN_URL}/merchandise`}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            BingoPort merch store
                        </a>{' '}
                        .
                    </p>
                )}
                <h3>Dauber color</h3>
                <div className={styles.circles}>
                    {colors.map(
                        ({ name, slug }: { name: string; slug: string }) => {
                            return (
                                <ToggleCircle
                                    key={slug}
                                    text={name}
                                    backgroundColor={slug}
                                    name="dauber_color"
                                    value={slug}
                                    checked={
                                        user.dauber_color === slug
                                            ? true
                                            : false
                                    }
                                    disabled={false}
                                    onChange={() =>
                                        handleSettingChange(
                                            'dauber_color',
                                            slug,
                                        )
                                    }
                                />
                            );
                        },
                    )}
                </div>
                <h3>Dauber icon</h3>
                <div className={styles.circles}>
                    {iconModsExpanded.map(
                        ({
                            id,
                            name,
                            slug,
                        }: {
                            id: number;
                            name: string;
                            slug: string;
                        }) => {
                            let disabled = true;

                            // If the dauber mod is part of the default set of dauber mods
                            if (
                                icons.filter((item) => item.id === id).length >
                                0
                            ) {
                                disabled = false;
                            }

                            // If the dauber mod is in the list of user's available dauber mods
                            if (
                                user.game_mods.filter(
                                    (item) =>
                                        parseInt(
                                            item.replace(modIdPrefix, ''),
                                        ) === id,
                                ).length > 0
                            ) {
                                disabled = false;
                            }
                            return slug === '' ? (
                                <ToggleCircle
                                    key={id}
                                    text={name}
                                    backgroundColor={user.dauber_color}
                                    name="dauber_icon"
                                    value={slug}
                                    checked={
                                        user.dauber_icon === slug ? true : false
                                    }
                                    disabled={false}
                                    onChange={() =>
                                        handleSettingChange('dauber_icon', slug)
                                    }
                                />
                            ) : (
                                <ToggleCircle
                                    key={id}
                                    text={name}
                                    backgroundImage={`https://${process.env.REACT_APP_CDN}/play-bingo/images/daubers/${slug}.svg`}
                                    name="dauber_icon"
                                    value={slug}
                                    checked={
                                        user.dauber_icon === slug ? true : false
                                    }
                                    disabled={disabled}
                                    onChange={() =>
                                        handleSettingChange('dauber_icon', slug)
                                    }
                                />
                            );
                        },
                    )}
                </div>
                {userIconMods.length !== iconMods.length && (
                    <p>
                        You can purchase more dauber icons at the{' '}
                        <a
                            href={`https://${process.env.REACT_APP_LOGIN_URL}/merchandise`}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            BingoPort merch store
                        </a>{' '}
                        .
                    </p>
                )}
                {/*
        {user.external_member_level > 1 ? (
          <></>
        ) : (
          <p>
            You must be a BingoPort Bulletin subscriber in order to use custom
            dauber icons.
          </p>
        )}
        */}
            </div>
        </Menu>
    );
}

// Exports
export default Settings;
